import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon, PaperAirplaneIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '../store/authStore';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/api';

// Sample queries for "Try asking about" suggestions
const SAMPLE_QUERIES = [
  'How should I start investing?',
  'What is a Roth IRA?',
  'Help me create a budget',
  'How to improve my credit score?',
  'Best way to pay off debt',
];

export const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('common:greeting.morning', 'Good morning');
    if (hour < 18) return t('common:greeting.afternoon', 'Good afternoon');
    return t('common:greeting.evening', 'Good evening');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!query.trim()) {
      return;
    }

    setLoading(true);
    setErrorMessage(null);

    try {
      // Call the classify endpoint
      const response = await apiClient.post('/classify/query', {
        query: query.trim()
      });

      const activity = response.data?.activity;

      // Map activity to route (must match routes in App.tsx)
      const activityRoutes: Record<string, string> = {
        'Ask Questions': '/ask-questions',
        'Financial Glossary': '/financial-glossary',
        'Financial Calculators': '/calculators',
        'Budget Tracker': '/budget',
        'Net Worth': '/net-worth',
        'Debt Manager': '/debt',
        'Statement Analyzer': '/statements',
        'Portfolio Analyzer': '/portfolio',
        'Credit Analyzer': '/credit',
        'Loan Planning': '/loans',
        'Tax Planning': '/taxes',
        'Financial Goals': '/goals',
        'Financial Documents': '/documents',
        'AI Financial Advisor': '/advisor',
      };

      const route = activityRoutes[activity];

      if (route) {
        // Navigate to the appropriate activity with context
        setRetryCount(0);
        navigate(route, { state: { initialQuery: query.trim() } });
      } else {
        // No appropriate activity found
        if (retryCount === 0) {
          // First attempt - ask user to be more specific
          setErrorMessage('I couldn\'t determine the best activity for your query. Please try being more specific about what you need help with.');
          setRetryCount(1);
        } else {
          // Second attempt - direct to sidebar
          setErrorMessage('I\'m still unable to find a matching activity. Please browse the menu in the sidebar to select the activity that best fits your needs.');
          setRetryCount(0);
        }
      }
    } catch (error) {
      console.error('Error classifying query:', error);
      setErrorMessage('An error occurred while processing your request. Please try again or select an activity from the sidebar.');
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
    setErrorMessage(null);
    setRetryCount(0);
  };

  return (
    <div className="flex flex-col items-center justify-center h-full p-8">
      {/* Logo */}
      <div className="mb-8">
        <img src="/dollar-sign.png" alt="Auxilio Finance" className="h-24 w-24" />
      </div>

      {/* Greeting */}
      <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">
        {getGreeting()}, {user?.name || user?.email?.split('@')[0] || 'there'}
      </h1>
      <p className="text-xl text-gray-600 dark:text-gray-400 mb-12">
        {t('common:dashboard.helpPrompt', 'What can I help you with today?')}
      </p>

      {/* Search Bar */}
      <form onSubmit={handleSubmit} className="w-full max-w-3xl">
        <div className="relative flex items-center">
          <div className="absolute left-4">
            <MagnifyingGlassIcon className="h-6 w-6 text-gray-400" />
          </div>
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              if (errorMessage) setErrorMessage(null);
            }}
            placeholder={t('dashboard:searchPlaceholder', 'Ask a financial question or describe what you need...')}
            className="w-full pl-12 pr-12 py-4 text-lg border-2 border-gray-300 dark:border-gray-600 rounded-full focus:outline-none focus:border-amber-500 dark:focus:border-amber-400 transition-colors bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
          <div className="absolute right-4 flex items-center space-x-2">
            <button
              type="submit"
              disabled={loading || !query.trim()}
              className="p-2 text-amber-500 hover:text-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PaperAirplaneIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="mt-4 flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <ExclamationCircleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800 dark:text-amber-200">{errorMessage}</p>
          </div>
        )}
      </form>

      {/* Try asking about suggestions */}
      <div className="mt-8 text-center w-full max-w-3xl">
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
          {t('dashboard:tryAsking', 'Try asking about:')}
        </p>
        <div className="flex flex-wrap justify-center gap-2">
          {SAMPLE_QUERIES.map((suggestion) => (
            <button
              key={suggestion}
              onClick={() => handleSuggestionClick(suggestion)}
              className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full hover:bg-amber-100 dark:hover:bg-amber-900/30 hover:text-amber-700 dark:hover:text-amber-300 transition-colors"
            >
              {suggestion}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
